import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Target,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle2,
  Pause,
  Play,
  X,
  Calendar,
  User,
  Link2,
  AlertTriangle,
  Zap,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';
import GoalEditor from './GoalEditor';

const PRIORITY_CONFIG = {
  1: { label: 'Urgent', color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', icon: Zap },
  2: { label: 'High', color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', icon: ArrowUp },
  3: { label: 'Normal', color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', icon: null },
  4: { label: 'Low', color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/30', icon: ArrowDown },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-blue-500', textColor: 'text-blue-700 dark:text-blue-300' },
  completed: { label: 'Completed', color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-300' },
  paused: { label: 'Paused', color: 'bg-yellow-500', textColor: 'text-yellow-700 dark:text-yellow-300' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500', textColor: 'text-gray-700 dark:text-gray-300' },
};

export default function ProjectGoalsTab({ project }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [viewingGoal, setViewingGoal] = useState(null);
  const [deleteConfirmGoal, setDeleteConfirmGoal] = useState(null);

  const loadGoals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${project.id}/goals`);
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to load project goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const handleCreateGoal = async (goalData) => {
    try {
      await api.post(`/projects/${project.id}/goals`, goalData);
      toast.success('Goal created');
      setIsCreateDialogOpen(false);
      loadGoals();
    } catch (error) {
      console.error('Failed to create goal:', error);
      toast.error('Failed to create goal');
    }
  };

  const handleUpdateGoal = async (goalData) => {
    try {
      await api.put(`/projects/${project.id}/goals/${editingGoal.id}`, goalData);
      toast.success('Goal updated');
      setEditingGoal(null);
      loadGoals();
    } catch (error) {
      console.error('Failed to update goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async () => {
    if (!deleteConfirmGoal) return;
    try {
      await api.delete(`/projects/${project.id}/goals/${deleteConfirmGoal.id}`);
      toast.success('Goal deleted');
      setDeleteConfirmGoal(null);
      loadGoals();
    } catch (error) {
      console.error('Failed to delete goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const handleStatusChange = async (goal, newStatus) => {
    try {
      const updates = { status: newStatus };
      if (newStatus === 'completed') {
        updates.progress_percent = 100;
      }
      await api.put(`/projects/${project.id}/goals/${goal.id}`, updates);
      toast.success(`Goal ${newStatus === 'completed' ? 'completed' : 'status updated'}`);
      loadGoals();
    } catch (error) {
      console.error('Failed to update goal status:', error);
      toast.error('Failed to update goal');
    }
  };

  const isOverdue = (goal) => {
    if (!goal.target_date || goal.status === 'completed' || goal.status === 'cancelled') {
      return false;
    }
    return new Date(goal.target_date) < new Date();
  };

  // Group goals by status for display
  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => g.status === 'completed');
  const pausedGoals = goals.filter(g => g.status === 'paused');
  const cancelledGoals = goals.filter(g => g.status === 'cancelled');

  const renderGoalCard = (goal) => {
    const priorityConfig = PRIORITY_CONFIG[goal.priority] || PRIORITY_CONFIG[3];
    const statusConfig = STATUS_CONFIG[goal.status] || STATUS_CONFIG.active;
    const PriorityIcon = priorityConfig.icon;
    const overdue = isOverdue(goal);

    return (
      <Card
        key={goal.id}
        className={cn(
          "p-4 hover:shadow-md transition-shadow",
          goal.status === 'completed' && "opacity-75",
          overdue && "border-red-500/50"
        )}
        data-testid={`goal-${goal.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {goal.status === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <Target className={cn("h-5 w-5", priorityConfig.color)} />
            )}
            <Badge variant="secondary" className={cn("text-xs", priorityConfig.bg, priorityConfig.color)}>
              {PriorityIcon && <PriorityIcon className="h-3 w-3 mr-1" />}
              {priorityConfig.label}
            </Badge>
            {overdue && (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Overdue
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditingGoal(goal)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {goal.status !== 'completed' && (
                <DropdownMenuItem onClick={() => handleStatusChange(goal, 'completed')}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </DropdownMenuItem>
              )}
              {goal.status === 'active' && (
                <DropdownMenuItem onClick={() => handleStatusChange(goal, 'paused')}>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </DropdownMenuItem>
              )}
              {goal.status === 'paused' && (
                <DropdownMenuItem onClick={() => handleStatusChange(goal, 'active')}>
                  <Play className="h-4 w-4 mr-2" />
                  Resume
                </DropdownMenuItem>
              )}
              {goal.status !== 'cancelled' && goal.status !== 'completed' && (
                <DropdownMenuItem onClick={() => handleStatusChange(goal, 'cancelled')}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteConfirmGoal(goal)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <h3 className={cn(
          "font-semibold mb-2",
          goal.status === 'completed' && "line-through text-muted-foreground"
        )}>
          {goal.title}
        </h3>

        {goal.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {goal.description}
          </p>
        )}

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className={cn(
              "font-medium",
              goal.progress_percent >= 80 ? "text-green-500" :
              goal.progress_percent >= 50 ? "text-blue-500" :
              goal.progress_percent >= 25 ? "text-yellow-500" :
              "text-muted-foreground"
            )}>
              {goal.progress_percent}%
            </span>
          </div>
          <Progress
            value={goal.progress_percent}
            className={cn(
              "h-2",
              goal.status === 'completed' && "bg-green-100 dark:bg-green-900/30"
            )}
          />
        </div>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {goal.target_date && (
            <div className={cn("flex items-center gap-1", overdue && "text-red-500")}>
              <Calendar className="h-3 w-3" />
              {formatDate(goal.target_date)}
            </div>
          )}
          {(goal.linked_issues_count > 0 || goal.linked_prs_count > 0) && (
            <div className="flex items-center gap-1">
              <Link2 className="h-3 w-3" />
              {goal.linked_issues_count > 0 && `${goal.linked_issues_count} issues`}
              {goal.linked_issues_count > 0 && goal.linked_prs_count > 0 && ', '}
              {goal.linked_prs_count > 0 && `${goal.linked_prs_count} PRs`}
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="project-goals-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Project Goals</h2>
          <p className="text-sm text-muted-foreground">
            Track progress towards key objectives for {project.name}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-goal-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Goal
        </Button>
      </div>

      {/* Summary Stats */}
      {!loading && goals.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-500">{activeGoals.length}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-500">{completedGoals.length}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-yellow-500">{pausedGoals.length}</div>
            <div className="text-sm text-muted-foreground">Paused</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold">
              {goals.length > 0
                ? Math.round(goals.reduce((sum, g) => sum + g.progress_percent, 0) / goals.length)
                : 0}%
            </div>
            <div className="text-sm text-muted-foreground">Avg Progress</div>
          </Card>
        </div>
      )}

      {/* Goals List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3" />
              <div className="h-3 bg-muted rounded w-full mb-2" />
              <div className="h-2 bg-muted rounded w-full mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </Card>
          ))}
        </div>
      ) : goals.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            No goals set for this project yet
          </p>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Goal
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Goals */}
          {activeGoals.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                Active Goals ({activeGoals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeGoals.map(renderGoalCard)}
              </div>
            </div>
          )}

          {/* Paused Goals */}
          {pausedGoals.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                Paused Goals ({pausedGoals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pausedGoals.map(renderGoalCard)}
              </div>
            </div>
          )}

          {/* Completed Goals */}
          {completedGoals.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Completed Goals ({completedGoals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {completedGoals.map(renderGoalCard)}
              </div>
            </div>
          )}

          {/* Cancelled Goals */}
          {cancelledGoals.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-500" />
                Cancelled Goals ({cancelledGoals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cancelledGoals.map(renderGoalCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Goal Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Goal to {project.name}</DialogTitle>
          </DialogHeader>
          <GoalEditor
            projectId={project.id}
            onSave={handleCreateGoal}
            onCancel={() => setIsCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={!!editingGoal} onOpenChange={(open) => !open && setEditingGoal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Goal</DialogTitle>
          </DialogHeader>
          {editingGoal && (
            <GoalEditor
              goal={editingGoal}
              projectId={project.id}
              onSave={handleUpdateGoal}
              onCancel={() => setEditingGoal(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmGoal} onOpenChange={(open) => !open && setDeleteConfirmGoal(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Goal?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this goal and all its linked resources. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGoal}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
